/**
 * Colonnes et lignes d'un tableau — les deux listes ne peuvent pas bouger
 * l'une sans l'autre : une ligne porte **une cellule par colonne, dans
 * l'ordre**. Ajouter, supprimer, déplacer ou dupliquer une colonne oblige donc
 * à réécrire toutes les lignes.
 *
 * La liste de la sidebar (`CollapsibleListField`) ne dit pas quelle opération
 * l'utilisateur a faite : elle rend le nouveau tableau, point. On reconstitue
 * l'opération en comparant les **clés** de colonne, ce qui couvre les quatre
 * cas d'un coup.
 */

const DEFAULT_COLUMN_WIDTH_TOTAL = 100;

/** Clé stable et unique pour une colonne. */
export function createColumnKey(taken = new Set()) {
  let index = taken.size + 1;
  let key = `col_${index}`;
  while (taken.has(key)) {
    index += 1;
    key = `col_${index}`;
  }
  return key;
}

/** Une colonne neuve : le libellé s'écrit ensuite sur le canvas. */
export function createColumn(existingColumns = []) {
  const taken = new Set(existingColumns.map((col) => col?.key).filter(Boolean));
  return {
    key: createColumnKey(taken),
    label: `Colonne ${existingColumns.length + 1}`,
    widthPercent: null,
    cellType: 'text',
    textAlign: '',
    verticalAlign: '',
  };
}

/** Une ligne neuve : autant de cellules vides que de colonnes. */
export function createRow(columnCount) {
  return { cells: Array.from({ length: Math.max(columnCount, 0) }, () => ({ value: '' })) };
}

/**
 * Réaligne les lignes sur la nouvelle liste de colonnes.
 *
 * - colonne conservée ou déplacée → sa cellule suit ;
 * - colonne ajoutée → cellule vide ;
 * - colonne supprimée → cellule perdue, c'est ce que l'utilisateur demande ;
 * - colonne dupliquée → **cellules recopiées** depuis l'originale, et clé neuve
 *   (deux colonnes de même clé casseraient et les clés React, et ce calcul).
 *
 * @returns {{ columns: object[], rows: object[] }}
 */
export function reconcileColumnsChange(previousColumns, nextColumns, rows) {
  const previous = Array.isArray(previousColumns) ? previousColumns : [];
  const next = Array.isArray(nextColumns) ? nextColumns : [];
  const taken = new Set();
  const columns = [];
  /** Index de la colonne d'origine pour chaque nouvelle colonne, ou -1. */
  const sources = [];

  next.forEach((column) => {
    const sourceIndex = previous.findIndex((col) => col?.key && col.key === column?.key);
    const isDuplicate = !column?.key || taken.has(column.key);
    const key = isDuplicate ? createColumnKey(new Set([...taken, ...previous.map((c) => c?.key)])) : column.key;
    taken.add(key);
    columns.push(isDuplicate ? { ...column, key } : column);
    sources.push(sourceIndex);
  });

  const nextRows = (Array.isArray(rows) ? rows : []).map((row) => ({
    ...row,
    cells: sources.map((sourceIndex) => (
      sourceIndex >= 0 ? (row?.cells?.[sourceIndex] || {}) : {}
    )),
  }));

  return { columns, rows: nextRows };
}

/**
 * Répartit les largeurs manquantes à parts égales.
 *
 * Une colonne ajoutée n'a pas de largeur ; sans ça, les colonnes réglées
 * gardent leur pourcentage et la nouvelle prend tout le reste.
 */
export function distributeMissingWidths(columns) {
  const missing = columns.filter((col) => col.widthPercent == null);
  if (!missing.length) return columns;

  const used = columns.reduce((sum, col) => sum + (Number(col.widthPercent) || 0), 0);
  const free = Math.max(DEFAULT_COLUMN_WIDTH_TOTAL - used, 0);
  const share = Number((free / missing.length).toFixed(2));

  return columns.map((col) => (
    col.widthPercent == null ? { ...col, widthPercent: share } : col
  ));
}
